import type { InputHTMLAttributes } from 'react'
import { parseFlexibleDate } from '../utils/parseFlexibleDate'

type DateInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange' | 'onPaste'
> & {
  /** Current value as ISO `YYYY-MM-DD` (what the native date control expects). */
  value: string
  /**
   * Called with the new ISO value — whether the user picked it in the native
   * control or pasted a looser US format. Pastes are run through
   * `parseFlexibleDate`; on success the normalized ISO is emitted, otherwise the
   * paste falls through to the browser's default handling.
   */
  onValueChange: (value: string) => void
}

/**
 * Native `<input type="date">` augmented with paste normalization.
 *
 * The native control only accepts ISO and silently rejects a pasted `11/1/98`
 * or `1998-11-01`; this wrapper parses such pastes via `parseFlexibleDate` and
 * writes the ISO result instead. Shared by the CSV review grid (DOB + Date of
 * Service), the manual eligibility form, and the medical slideout's identity
 * editor. All other input props (id, className, onKeyDown, disabled, autoFocus,
 * aria-label, …) pass straight through.
 */
export default function DateInput({ value, onValueChange, ...rest }: DateInputProps) {
  return (
    <input
      {...rest}
      type="date"
      value={value}
      onChange={e => onValueChange(e.target.value)}
      onPaste={e => {
        const parsed = parseFlexibleDate(e.clipboardData.getData('text'))
        if (parsed) {
          e.preventDefault()
          onValueChange(parsed)
        }
      }}
    />
  )
}
